/**
 * useKeyboardShortcuts Hook
 *
 * Provides comprehensive keyboard shortcut management for LegalSyncUp
 * Features:
 * - Global and context-aware shortcuts
 * - Conflict prevention
 * - Automatic cleanup
 * - Platform detection (Mac vs Windows/Linux)
 *
 * @example
 * ```tsx
 * const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
 *
 * useEffect(() => {
 *   const id = registerShortcut({
 *     key: 's',
 *     ctrl: true,
 *     description: 'Save document',
 *     handler: () => saveDocument(),
 *     context: 'document-editor'
 *   });
 *
 *   return () => unregisterShortcut(id);
 * }, []);
 * ```
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router';

export interface KeyboardShortcut {
  /** Unique identifier (auto-generated if not provided) */
  id?: string;
  /** Primary key (e.g., 's', 'k', 'Escape') */
  key: string;
  /** Ctrl/Cmd modifier */
  ctrl?: boolean;
  /** Alt/Option modifier */
  alt?: boolean;
  /** Shift modifier */
  shift?: boolean;
  /** Meta (Windows/Command) modifier */
  meta?: boolean;
  /** Human-readable description */
  description: string;
  /** Callback function to execute */
  handler: (event: KeyboardEvent) => void;
  /** Context where this shortcut is active (page path or 'global') */
  context?: string;
  /** Whether to prevent default browser behavior */
  preventDefault?: boolean;
  /** Whether to stop event propagation */
  stopPropagation?: boolean;
  /** Whether the shortcut is enabled */
  enabled?: boolean;
  /** Group/category for help display */
  group?: string;
}

interface ShortcutRegistry {
  [id: string]: KeyboardShortcut;
}

// Detect platform for displaying correct modifier keys
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

// Singleton registry to store all shortcuts
const shortcutRegistry: ShortcutRegistry = {};

/**
 * Generate a unique shortcut ID
 */
function generateShortcutId(): string {
  return `shortcut-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Normalize key for comparison (lowercase, handle special keys)
 */
function normalizeKey(key: string): string {
  const specialKeys: Record<string, string> = {
    'Esc': 'Escape',
    'Return': 'Enter',
    'Space': ' ',
    'Del': 'Delete',
    'Left': 'ArrowLeft',
    'Right': 'ArrowRight',
    'Up': 'ArrowUp',
    'Down': 'ArrowDown',
  };

  return specialKeys[key] || key.toLowerCase();
}

/**
 * Check if keyboard event matches a shortcut definition
 */
function matchesShortcut(event: KeyboardEvent, shortcut: KeyboardShortcut): boolean {
  const eventKey = normalizeKey(event.key);
  const shortcutKey = normalizeKey(shortcut.key);

  if (eventKey !== shortcutKey) return false;

  // Check modifiers
  const ctrlMatch = !shortcut.ctrl || (isMac ? event.metaKey : event.ctrlKey);
  const altMatch = !shortcut.alt || event.altKey;
  const shiftMatch = !shortcut.shift || event.shiftKey;
  const metaMatch = !shortcut.meta || event.metaKey;

  // Ensure modifiers that shouldn't be pressed aren't pressed
  const noExtraCtrl = shortcut.ctrl || !(isMac ? event.metaKey : event.ctrlKey);
  const noExtraAlt = shortcut.alt || !event.altKey;
  const noExtraShift = shortcut.shift || !event.shiftKey;
  const noExtraMeta = shortcut.meta || !event.metaKey;

  return ctrlMatch && altMatch && shiftMatch && metaMatch &&
         noExtraCtrl && noExtraAlt && noExtraShift && noExtraMeta;
}

/**
 * Format shortcut for display (e.g., "Cmd+K" or "Ctrl+K")
 */
export function formatShortcut(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];

  if (shortcut.ctrl) {
    parts.push(isMac ? 'Cmd' : 'Ctrl');
  }
  if (shortcut.alt) {
    parts.push(isMac ? 'Opt' : 'Alt');
  }
  if (shortcut.shift) {
    parts.push('Shift');
  }
  if (shortcut.meta) {
    parts.push(isMac ? 'Cmd' : 'Win');
  }

  // Capitalize special keys
  const key = shortcut.key === ' ' ? 'Space' : shortcut.key;
  const formattedKey = key.length === 1 ? key.toUpperCase() : key;
  parts.push(formattedKey);

  return parts.join('+');
}

/**
 * Get all shortcuts for the current context
 */
export function getShortcutsForContext(context: string): KeyboardShortcut[] {
  return Object.values(shortcutRegistry).filter(
    (shortcut) => shortcut.enabled !== false && (shortcut.context === context || shortcut.context === 'global')
  );
}

/**
 * Get all registered shortcuts grouped by category
 */
export function getAllShortcuts(): Record<string, KeyboardShortcut[]> {
  const grouped: Record<string, KeyboardShortcut[]> = {
    'Global': [],
    'Navigation': [],
    'Documents': [],
    'Entities': [],
    'Other': [],
  };

  Object.values(shortcutRegistry).forEach((shortcut) => {
    const group = shortcut.group || 'Other';
    if (!grouped[group]) {
      grouped[group] = [];
    }
    grouped[group].push(shortcut);
  });

  return grouped;
}

export interface UseKeyboardShortcutsOptions {
  /** Whether keyboard shortcuts are enabled globally */
  enabled?: boolean;
}

export interface UseKeyboardShortcutsReturn {
  /** Register a new keyboard shortcut */
  registerShortcut: (shortcut: KeyboardShortcut) => string;
  /** Unregister a keyboard shortcut by ID */
  unregisterShortcut: (id: string) => void;
  /** Update an existing shortcut */
  updateShortcut: (id: string, updates: Partial<KeyboardShortcut>) => void;
  /** Get all shortcuts for current context */
  getActiveShortcuts: () => KeyboardShortcut[];
  /** Format a shortcut for display */
  formatShortcut: (shortcut: KeyboardShortcut) => string;
}

export function useKeyboardShortcuts(
  options: UseKeyboardShortcutsOptions = {}
): UseKeyboardShortcutsReturn {
  const { enabled = true } = options;
  const location = useLocation();
  const currentContext = location.pathname;
  const localShortcutsRef = useRef<Set<string>>(new Set());

  /**
   * Register a keyboard shortcut
   */
  const registerShortcut = useCallback((shortcut: KeyboardShortcut): string => {
    const id = shortcut.id || generateShortcutId();

    // Check for conflicts
    const existingShortcut = Object.values(shortcutRegistry).find((s) =>
      s.id !== id &&
      s.key === shortcut.key &&
      s.ctrl === shortcut.ctrl &&
      s.alt === shortcut.alt &&
      s.shift === shortcut.shift &&
      s.meta === shortcut.meta &&
      (s.context === shortcut.context || s.context === 'global' || shortcut.context === 'global')
    );

    if (existingShortcut) {
      console.warn(
        `Keyboard shortcut conflict: ${formatShortcut(shortcut)} is already registered for "${existingShortcut.description}"`
      );
    }

    shortcutRegistry[id] = {
      ...shortcut,
      id,
      enabled: shortcut.enabled !== false,
      preventDefault: shortcut.preventDefault !== false,
      stopPropagation: shortcut.stopPropagation !== false,
    };

    localShortcutsRef.current.add(id);
    return id;
  }, []);

  /**
   * Unregister a keyboard shortcut
   */
  const unregisterShortcut = useCallback((id: string) => {
    delete shortcutRegistry[id];
    localShortcutsRef.current.delete(id);
  }, []);

  /**
   * Update an existing shortcut
   */
  const updateShortcut = useCallback((id: string, updates: Partial<KeyboardShortcut>) => {
    if (shortcutRegistry[id]) {
      shortcutRegistry[id] = {
        ...shortcutRegistry[id],
        ...updates,
      };
    }
  }, []);

  /**
   * Get active shortcuts for current context
   */
  const getActiveShortcuts = useCallback((): KeyboardShortcut[] => {
    return getShortcutsForContext(currentContext);
  }, [currentContext]);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in inputs (unless it's Escape)
    const target = event.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.contentEditable === 'true';

    if (isInput && event.key !== 'Escape') {
      return;
    }

    // Get shortcuts for current context
    const activeShortcuts = getShortcutsForContext(currentContext);

    // Find matching shortcut
    for (const shortcut of activeShortcuts) {
      if (matchesShortcut(event, shortcut) && shortcut.enabled !== false) {
        if (shortcut.preventDefault) {
          event.preventDefault();
        }
        if (shortcut.stopPropagation) {
          event.stopPropagation();
        }

        try {
          shortcut.handler(event);
        } catch (error) {
          console.error(`Error executing shortcut ${formatShortcut(shortcut)}:`, error);
        }

        // Only execute the first matching shortcut
        break;
      }
    }
  }, [enabled, currentContext]);

  // Register global keyboard event listener
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  // Cleanup shortcuts registered by this hook on unmount
  useEffect(() => {
    const currentShortcuts = localShortcutsRef.current;
    return () => {
      currentShortcuts.forEach((id) => {
        delete shortcutRegistry[id];
      });
      currentShortcuts.clear();
    };
  }, []);

  return {
    registerShortcut,
    unregisterShortcut,
    updateShortcut,
    getActiveShortcuts,
    formatShortcut,
  };
}

/**
 * Hook to register shortcuts that are automatically cleaned up
 */
export function useShortcut(shortcut: KeyboardShortcut): void {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();

  useEffect(() => {
    const id = registerShortcut(shortcut);
    return () => unregisterShortcut(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcut.key, shortcut.ctrl, shortcut.alt, shortcut.shift, shortcut.meta, shortcut.enabled]);
}

export default useKeyboardShortcuts;
